// by freakms - ich schwöre feierlich ich bin ein tunichtgut
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import {
  RefreshCw,
  Plus,
  Package,
  History,
  Warehouse,
  AlertTriangle,
  Clock,
  Undo2,
  WifiOff,
  ScanBarcode,
  Tag,
  ChevronDown,
  Info
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { offlineApi } from "../lib/syncManager";
import BarcodeScanner from "../components/BarcodeScanner";

// ---- Hilfsfunktionen ----

const getMhdStatus = (expiryDate) => {
  if (!expiryDate || expiryDate === "1970-01-01") return null;
  const today = new Date();
  const expiry = new Date(expiryDate);
  const days = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
  if (days < 0) return { status: "expired", days, label: `${Math.abs(days)}T abgelaufen`, color: "text-red-700 bg-red-50 border-red-200" };
  if (days <= 14) return { status: "critical", days, label: `noch ${days}T`, color: "text-amber-700 bg-amber-50 border-amber-200" };
  if (days <= 30) return { status: "warning", days, label: `noch ${days}T`, color: "text-yellow-700 bg-yellow-50 border-yellow-200" };
  return { status: "ok", days, label: `noch ${days}T`, color: "text-emerald-700 bg-emerald-50 border-emerald-200" };
};

const formatDate = (d) => {
  if (!d || d === "1970-01-01") return null;
  return new Date(d).toLocaleDateString("de-DE");
};

// ---- MHD-Warnung nach Auffüllung ----

const showRefillSummary = async (refillInfo) => {
  try {
    const res = await axios.get(`${API}/notifications/summary`);
    const { expiring_products, low_stock_products } = res.data;
    toast.success(
      <div>
        <div className="font-medium">Auffüllung erfolgreich</div>
        <div className="text-sm mt-1">
          <span className="font-medium">Fach {refillInfo.slot_id}:</span> {refillInfo.product_name}
        </div>
        <div className="text-sm text-slate-500">
          {refillInfo.old_stock} → {refillInfo.new_stock} Stück (+{refillInfo.quantity})
        </div>
      </div>,
      { duration: 5000 }
    );
    if (expiring_products?.filter(p => p.status === "expired").length > 0) {
      const expired = expiring_products.filter(p => p.status === "expired");
      toast.error(
        <div>
          <div className="font-medium flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />{expired.length} Produkt(e) ABGELAUFEN!
          </div>
          <ul className="text-sm mt-1">{expired.slice(0, 3).map((p, i) => <li key={i}>• {p.product_name} ({p.location})</li>)}</ul>
        </div>,
        { duration: 8000 }
      );
    }
  } catch { /* silent */ }
};

// ---- Auffüll-Formular ----

const RefillForm = ({ compartments, products, batches, onSuccess }) => {
  const [compartmentId, setCompartmentId] = useState("");
  const [quantity, setQuantity] = useState("");
  const [source, setSource] = useState("warehouse");
  const [batchId, setBatchId] = useState("");
  const [saving, setSaving] = useState(false);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [conflictInfo, setConflictInfo] = useState(null);

  const selectedCompartment = compartments.find(c => c.id === compartmentId);
  const product = selectedCompartment ? products.find(p => p.id === selectedCompartment.product_id) : null;

  // Verfügbare Chargen für das gewählte Produkt (FIFO-sortiert)
  const availableBatches = product
    ? batches
        .filter(b => b.product_id === product.id && b.status === "active" && b.quantity > 0)
        .sort((a, b) => (a.expiry_date || "9999").localeCompare(b.expiry_date || "9999"))
    : [];

  const selectedBatch = availableBatches.find(b => b.id === batchId) || availableBatches[0];

  // Wenn Fach gewählt: Konfliktcheck
  useEffect(() => {
    setConflictInfo(null);
    setBatchId("");
    if (!product || !selectedBatch) return;
    axios.post(`${API}/inventory/check-mhd-conflict?product_id=${product.id}&expiry_date=${selectedBatch?.expiry_date || ""}`)
      .then(res => { if (res.data?.has_conflict) setConflictInfo(res.data); })
      .catch(() => {});
  }, [compartmentId]);

  const handleBarcodeScan = async (code) => {
    try {
      const res = await axios.get(`${API}/products/by-ean/${code}`);
      if (res.data) {
        const comp = compartments.find(c => c.product_id === res.data.id);
        if (comp) { setCompartmentId(comp.id); toast.success(`${res.data.name} → Fach ${comp.slot_id}`); }
        else toast.warning(`Produkt "${res.data.name}" hat kein Fach zugewiesen`);
      }
    } catch { toast.error(`Kein Produkt mit EAN ${code}`); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!compartmentId || !quantity || parseInt(quantity) <= 0) { toast.error("Bitte alle Felder ausfüllen"); return; }
    if (!product) { toast.error("Fach hat kein Produkt"); return; }

    const oldStock = selectedCompartment.current_stock;
    const newStock = Math.min(oldStock + parseInt(quantity), selectedCompartment.max_capacity);

    setSaving(true);
    try {
      await axios.post(`${API}/refills`, {
        compartment_id: compartmentId,
        product_id: product.id,
        product_name: product.name,
        slot_id: selectedCompartment.slot_id,
        quantity: parseInt(quantity),
        refill_type: "add",
        source,
        batch_id: batchId || null
      });
      await showRefillSummary({ slot_id: selectedCompartment.slot_id, product_name: product.name, old_stock: oldStock, new_stock: newStock, quantity: parseInt(quantity) });
      setCompartmentId("");
      setQuantity("");
      setBatchId("");
      setConflictInfo(null);
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Auffüllen");
    } finally {
      setSaving(false);
    }
  };

  const filledCompartments = compartments.filter(c => c.product_id).sort((a, b) => a.slot_id.localeCompare(b.slot_id));

  return (
    <form onSubmit={handleSubmit} className="space-y-4">

      {/* Fach-Auswahl */}
      <div className="space-y-2">
        <Label>Fach auswählen *</Label>
        <div className="flex gap-2">
          <Select value={compartmentId} onValueChange={setCompartmentId}>
            <SelectTrigger data-testid="refill-compartment-select" className="flex-1">
              <SelectValue placeholder="Fach wählen" />
            </SelectTrigger>
            <SelectContent>
              {filledCompartments.map((c) => {
                const prod = products.find(p => p.id === c.product_id);
                const displayName = c.custom_name || c.slot_id;
                const isDiscount = c.is_discount_slot;
                return (
                  <SelectItem key={c.id} value={c.id}>
                    {displayName}{isDiscount ? " 🏷️" : ""} – {prod?.name || "Unbekannt"} ({c.current_stock}/{c.max_capacity})
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
          <Button type="button" variant="outline" onClick={() => setScannerOpen(true)} title="Per Barcode scannen">
            <ScanBarcode className="w-5 h-5" />
          </Button>
        </div>
      </div>

      <BarcodeScanner isOpen={scannerOpen} onClose={() => setScannerOpen(false)} onScan={handleBarcodeScan} title="Produkt per Barcode scannen" />

      {/* Produkt-Info + Rabatt-Badge */}
      {selectedCompartment && product && (
        <div className="bg-slate-50 rounded-md p-3 space-y-2">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-medium">{product.name}</div>
              <div className="text-sm text-slate-500">
                Aktuell: {selectedCompartment.current_stock} / {selectedCompartment.max_capacity}
              </div>
            </div>
            <div className="text-right">
              {selectedCompartment.price_override ? (
                <div>
                  <div className="text-lg font-heading font-bold text-amber-600">
                    {selectedCompartment.price_override.toFixed(2)}€
                  </div>
                  <div className="text-xs text-slate-400 line-through">{product.price.toFixed(2)}€</div>
                  <Badge variant="outline" className="text-amber-600 border-amber-300 text-xs">Rabattfach</Badge>
                </div>
              ) : (
                <div className="text-lg font-heading font-bold text-rose-600">{product.price.toFixed(2)}€</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* MHD-Konflikt-Warnung */}
      <AnimatePresence>
        {conflictInfo && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="bg-amber-50 border border-amber-300 rounded-md p-3 text-sm"
          >
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
              <div>
                <div className="font-medium text-amber-800">{conflictInfo.message}</div>
                {conflictInfo.recommended_discount_slots?.length > 0 && (
                  <div className="mt-1 text-amber-700">
                    Empfohlene Rabattfächer: <strong>{conflictInfo.recommended_discount_slots.join(", ")}</strong>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Chargen-Auswahl */}
      {availableBatches.length > 0 && source === "warehouse" && (
        <div className="space-y-2">
          <Label className="flex items-center gap-1">
            Charge
            <span className="text-xs text-slate-400 font-normal">(Standard: älteste zuerst)</span>
          </Label>
          <Select value={batchId} onValueChange={setBatchId}>
            <SelectTrigger data-testid="refill-batch-select">
              <SelectValue placeholder="Automatisch (FIFO)" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="">Automatisch – älteste Charge (FIFO)</SelectItem>
              {availableBatches.map((b) => {
                const mhd = getMhdStatus(b.expiry_date);
                return (
                  <SelectItem key={b.id} value={b.id}>
                    MHD {formatDate(b.expiry_date) || "offen"} · {b.quantity} Stück
                    {mhd && ` · ${mhd.label}`}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>

          {/* Aktive Charge anzeigen */}
          {selectedBatch && (
            <div className={`text-xs px-3 py-2 rounded border flex items-center gap-2 ${getMhdStatus(selectedBatch.expiry_date)?.color || "bg-slate-50 border-slate-200 text-slate-600"}`}>
              <Info className="w-3 h-3 shrink-0" />
              Verwendet: MHD {formatDate(selectedBatch.expiry_date) || "offen"} · {selectedBatch.quantity} Stück im Lager
            </div>
          )}
        </div>
      )}

      {/* Keine Charge verfügbar */}
      {product && availableBatches.length === 0 && source === "warehouse" && (
        <div className="text-sm bg-red-50 border border-red-200 text-red-700 rounded p-3 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          Kein Lagerbestand für dieses Produkt. Bitte zuerst Wareneingang buchen.
        </div>
      )}

      {/* Menge + Quelle */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Menge *</Label>
          <Input
            type="number" min="1"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            placeholder="z.B. 5"
            data-testid="refill-quantity-input"
          />
        </div>
        <div className="space-y-2">
          <Label>Quelle</Label>
          <Select value={source} onValueChange={setSource}>
            <SelectTrigger data-testid="refill-source-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="warehouse">Aus Lager</SelectItem>
              <SelectItem value="direct">Direktlieferung</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <Button
        type="submit"
        disabled={saving || !compartmentId || !quantity || (source === "warehouse" && availableBatches.length === 0)}
        className="w-full bg-rose-600 hover:bg-rose-700"
        data-testid="refill-submit-btn"
      >
        {saving ? "Auffüllen..." : "Auffüllen"}
      </Button>
    </form>
  );
};

// ---- Auffüll-Protokoll-Zeile ----

const RefillHistoryItem = ({ refill, onUndo }) => {
  const [undoing, setUndoing] = useState(false);
  const mhd = getMhdStatus(refill.batch_expiry);

  const handleUndo = async () => {
    setUndoing(true);
    try {
      await axios.delete(`${API}/refills/${refill.id}`);
      toast.success("Auffüllung rückgängig gemacht");
      onUndo();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Rückgängigmachen");
    } finally {
      setUndoing(false);
    }
  };

  return (
    <div className="flex items-center gap-4 py-3 border-b border-slate-100 last:border-0">
      <div className="w-10 h-10 rounded-md bg-emerald-100 flex items-center justify-center">
        <Plus className="w-5 h-5 text-emerald-600" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-mono text-sm bg-slate-100 px-2 py-0.5 rounded">{refill.slot_id}</span>
          <span className="font-medium truncate">{refill.product_name}</span>
          {refill.was_discounted && <Badge variant="outline" className="text-amber-600 border-amber-300 text-xs">Rabatt</Badge>}
        </div>
        <div className="text-sm text-slate-500 flex items-center gap-2 mt-1 flex-wrap">
          <span>+{refill.quantity} Stück</span>
          <span>·</span>
          <span className="flex items-center gap-1">
            {refill.source === "warehouse" ? <><Warehouse className="w-3 h-3" /> Aus Lager</> : "Direkt"}
          </span>
          {refill.batch_expiry && refill.batch_expiry !== "1970-01-01" && (
            <>
              <span>·</span>
              <span className={`text-xs px-1.5 py-0.5 rounded border ${mhd?.color || "text-slate-500 border-slate-200"}`}>
                MHD {formatDate(refill.batch_expiry)}
              </span>
            </>
          )}
          {refill.price_used != null && (
            <>
              <span>·</span>
              <span className="text-slate-400">{refill.price_used.toFixed(2)}€/Stk</span>
            </>
          )}
          {refill.user_name && <><span>·</span><span className="text-slate-400">{refill.user_name}</span></>}
        </div>
      </div>
      <div className="text-right mr-2 shrink-0">
        <div className="text-sm text-slate-500">{new Date(refill.created_at).toLocaleDateString("de-DE")}</div>
        <div className="text-xs text-slate-400">
          {new Date(refill.created_at).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })}
        </div>
      </div>
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button variant="ghost" size="icon" className="text-slate-400 hover:text-rose-600 shrink-0" data-testid={`undo-refill-${refill.id}`}>
            <Undo2 className="w-4 h-4" />
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent className="max-w-[95vw] sm:max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-base">Rückgängig machen?</AlertDialogTitle>
            <AlertDialogDescription className="text-sm">
              {refill.product_name} ({refill.slot_id}) – {refill.quantity}×
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={handleUndo} disabled={undoing} className="bg-[#DC2626] hover:bg-[#B91C1C]">
              {undoing ? "..." : "Rückgängig"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// ---- Haupt-Seite ----

const RefillPage = () => {
  const [compartments, setCompartments] = useState([]);
  const [products, setProducts] = useState([]);
  const [batches, setBatches] = useState([]);
  const [refills, setRefills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);

  useEffect(() => {
    fetchData();
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => { window.removeEventListener("online", handleOnline); window.removeEventListener("offline", handleOffline); };
  }, []);

  const fetchData = async () => {
    try {
      const [compRes, prodRes, refillRes, batchRes] = await Promise.all([
        offlineApi.getCompartments(),
        offlineApi.getProducts(),
        offlineApi.getRefills(),
        axios.get(`${API}/inventory/batches`)
      ]);
      setCompartments(compRes.data);
      setProducts(prodRes.data);
      setRefills(refillRes.data);
      setBatches(batchRes.data);
      if (compRes.fromCache || prodRes.fromCache) toast.info("Offline-Daten geladen", { duration: 2000 });
    } catch {
      toast.error("Fehler beim Laden");
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rose-600" /></div>;

  const lowStockCompartments = compartments
    .filter(c => c.product_id && (c.current_stock / c.max_capacity) < 0.3)
    .sort((a, b) => (a.current_stock / a.max_capacity) - (b.current_stock / b.max_capacity));

  return (
    <div className="space-y-6" data-testid="refill-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold text-slate-900 tracking-tight">Auffüllen</h1>
          <p className="text-slate-500 mt-1">Automaten-Fächer befüllen</p>
        </div>
        {isOffline && (
          <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-300">
            <WifiOff className="w-3 h-3 mr-1" /> Offline-Modus
          </Badge>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Formular */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <RefreshCw className="w-5 h-5" /> Fach auffüllen
              </CardTitle>
            </CardHeader>
            <CardContent>
              {compartments.filter(c => c.product_id).length > 0 ? (
                <RefillForm compartments={compartments} products={products} batches={batches} onSuccess={fetchData} />
              ) : (
                <div className="text-center py-8">
                  <Package className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                  <p className="text-slate-500">Keine Fächer mit Produkten</p>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Niedrige Bestände */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.1 }}>
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2 text-amber-600">
                <Package className="w-5 h-5" /> Nachfüllen empfohlen ({lowStockCompartments.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {lowStockCompartments.length > 0 ? (
                <div className="space-y-2">
                  {lowStockCompartments.slice(0, 6).map((comp) => {
                    const product = products.find(p => p.id === comp.product_id);
                    const percent = Math.round((comp.current_stock / comp.max_capacity) * 100);
                    const batch = batches.find(b => b.id === comp.active_batch_id);
                    const mhd = getMhdStatus(batch?.expiry_date);
                    return (
                      <div key={comp.id} className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-md">
                        <span className="font-mono text-sm font-bold text-amber-700">{comp.custom_name || comp.slot_id}</span>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate text-sm">{product?.name || "Unbekannt"}</div>
                          <div className="text-xs text-amber-700">
                            {comp.current_stock}/{comp.max_capacity} ({percent}%)
                            {mhd && mhd.status !== "ok" && (
                              <span className={`ml-2 px-1.5 py-0.5 rounded border text-xs ${mhd.color}`}>
                                MHD {mhd.label}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {lowStockCompartments.length > 6 && (
                    <p className="text-sm text-slate-500 text-center pt-2">+{lowStockCompartments.length - 6} weitere</p>
                  )}
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="w-12 h-12 mx-auto bg-emerald-100 rounded-full flex items-center justify-center mb-4">
                    <Package className="w-6 h-6 text-emerald-600" />
                  </div>
                  <p className="text-emerald-600 font-medium">Alle Fächer gut gefüllt!</p>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Protokoll */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.2 }}>
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <History className="w-5 h-5" /> Auffüll-Protokoll
            </CardTitle>
          </CardHeader>
          <CardContent>
            {refills.length > 0 ? (
              <div className="divide-y divide-slate-100">
                {refills.map((refill) => (
                  <RefillHistoryItem key={refill.id} refill={refill} onUndo={fetchData} />
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <History className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                <p className="text-slate-500">Noch keine Auffüllungen protokolliert</p>
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default RefillPage;
